/**
 * Client Dynamic List Service
 *
 * Builds and issues the request declared by `x-ms-client-dynamic-list`, and maps
 * the reply into combobox options.
 *
 * The connector parser copies this extension verbatim and never inspects it, so
 * everything structural is validated here, as the request is built. Every failure
 * is closed: no request is issued and the caller keeps freeform entry.
 */

import { getArmBaseUrl } from './armConfig';
import type { DynamicOption } from './dynamicDataService';
import type {
  ClientDynamicBinding,
  ClientDynamicBindingTarget,
  ClientDynamicListConfig,
  ClientDynamicMethod,
} from '@/types/operationParameter';

/** Matches the page cap the connection-backed picker uses. */
const MAX_PAGINATION_PAGES = 10;

const METHODS: readonly ClientDynamicMethod[] = ['GET', 'POST'];
const LOCATIONS = ['path', 'query', 'body'] as const;
const CLIENT_TYPES = ['arm'] as const;

const PLACEHOLDER = /\{([^{}]*)\}/g;
const HAS_PLACEHOLDER = /\{[^{}]*\}/;

/**
 * Outcome of building a request. `waiting` is an ordinary authoring state — an
 * upstream field simply has no value yet — while `error` means the picker itself
 * is malformed and no value will ever make it work.
 */
export type ClientDynamicRequestPlan =
  | { status: 'ready'; url: string; method: ClientDynamicMethod; body?: unknown }
  | { status: 'waiting'; missing: string[] }
  | { status: 'error'; message: string };

/** A resolved binding value, or the reason it could not be resolved. */
type Resolution =
  | { kind: 'value'; value: unknown }
  | { kind: 'unresolved' }
  | { kind: 'unknown' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True when a string carries a designer token or workflow expression. Those
 * cannot be evaluated at authoring time, so the picker waits rather than sending
 * the literal `@{...}` to the service.
 */
function isExpression(value: unknown): boolean {
  return typeof value === 'string' && (value.startsWith('@') || value.includes('@{') || value.includes('${'));
}

/**
 * Reads a value out of a document by dot-notation path. Mirrors the
 * connection-backed picker so both kinds of picker are configured identically.
 */
function getValueByPath(source: unknown, path: string | undefined): unknown {
  if (!path || source === null || source === undefined) return source;

  let current: unknown = source;
  for (const part of path.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Decodes one RFC 6901 segment (`~1` is `/`, `~0` is `~`). */
function decodePointerSegment(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~');
}

/**
 * Validates the extension's shape. Returns an error message, or undefined when
 * the configuration is usable.
 */
export function validateClientDynamicList(config: ClientDynamicListConfig): string | undefined {
  if (!isRecord(config)) return 'The picker configuration must be an object.';

  if (!CLIENT_TYPES.includes(config.type)) {
    return `Unsupported client type '${String(config.type)}'. Supported: ${CLIENT_TYPES.join(', ')}.`;
  }

  const request = config.request;
  if (!isRecord(request)) return "The picker is missing a 'request' object.";

  const template = request.template;
  if (!isRecord(template)) return "The picker is missing a 'request.template' object.";

  if ('headers' in template || 'headers' in request || 'headers' in config) {
    return 'The picker declares headers. Authorization and Content-Type are owned by the portal.';
  }

  if (!METHODS.includes(template.method)) {
    return `Unsupported method '${String(template.method)}'. Supported: ${METHODS.join(', ')}.`;
  }

  const path = template.path;
  if (typeof path !== 'string' || path.length === 0) {
    return "The picker is missing 'request.template.path'.";
  }

  if (path.includes('://') || path.startsWith('//') || !path.startsWith('/')) {
    return `The path '${path}' must be relative and start with '/'; the origin is resolved from the client type.`;
  }

  if (template.body !== undefined && template.method === 'GET') {
    return 'The picker declares a body on a GET request.';
  }

  const queryError = validateQuery(template.query);
  if (queryError) return queryError;

  const response = config.response;
  if (!isRecord(response)) return "The picker is missing a 'response' object.";

  for (const key of ['itemTitlePath', 'itemValuePath'] as const) {
    if (typeof response[key] !== 'string' || (response[key] as string).length === 0) {
      return `The picker is missing 'response.${key}'.`;
    }
  }

  for (const key of ['itemsPath', 'itemDescriptionPath', 'itemSelectablePath'] as const) {
    const value = response[key];
    if (value !== undefined && (typeof value !== 'string' || value.length === 0)) {
      return `The picker declares 'response.${key}', which must be a non-empty string.`;
    }
  }

  return validateBindings(request.bindings, template.body, path);
}

function validateQuery(query: unknown): string | undefined {
  if (query === undefined) return undefined;
  if (!isRecord(query)) return "'request.template.query' must be an object.";

  for (const [name, value] of Object.entries(query)) {
    if (!isScalar(value) && !(Array.isArray(value) && value.every(isScalar))) {
      return `The static query parameter '${name}' must be a string, number, boolean, or an array of those.`;
    }
  }

  return undefined;
}

function validateBindings(bindings: unknown, templateBody: unknown, path: string): string | undefined {
  if (bindings === undefined) return validatePlaceholders(path, new Set());
  if (!Array.isArray(bindings)) return "'request.bindings' must be an array.";

  const boundPathNames = new Set<string>();
  const seenTargets = new Set<string>();

  for (const binding of bindings as ClientDynamicBinding[]) {
    if (!isRecord(binding)) return 'A binding must be an object.';

    if (typeof binding.parameterReference !== 'string' || binding.parameterReference.length === 0) {
      return "A binding is missing 'parameterReference'.";
    }

    const target = binding.target;
    if (!isRecord(target)) return `The binding for '${binding.parameterReference}' is missing a 'target'.`;

    if (!LOCATIONS.includes(target.location as (typeof LOCATIONS)[number])) {
      return `The binding for '${binding.parameterReference}' declares location '${String(target.location)}'. Supported: ${LOCATIONS.join(', ')}.`;
    }

    const identity = targetIdentity(target as ClientDynamicBindingTarget);
    if (seenTargets.has(identity)) {
      return `More than one binding writes to ${identity}.`;
    }
    seenTargets.add(identity);

    if (target.location === 'body') {
      if (typeof target.pointer !== 'string' || !target.pointer.startsWith('/')) {
        return `The binding for '${binding.parameterReference}' declares pointer '${String(target.pointer)}'. A JSON Pointer must start with '/'.`;
      }
      const pointerError = validatePointer(target.pointer, templateBody);
      if (pointerError) return pointerError;
    } else {
      if (typeof target.name !== 'string' || target.name.length === 0) {
        return `The binding for '${binding.parameterReference}' is missing 'target.name'.`;
      }
      if (target.location === 'path') {
        if (binding.required === false) {
          return `The path binding '${target.name}' cannot be optional; a collapsed path segment is ambiguous.`;
        }
        boundPathNames.add(target.name);
      }
    }
  }

  return validatePlaceholders(path, boundPathNames);
}

function validatePointer(pointer: string, templateBody: unknown): string | undefined {
  if (templateBody === undefined) {
    return `The binding writes to '${pointer}' but the template declares no body.`;
  }

  const segments = pointer.slice(1).split('/').map(decodePointerSegment);

  if (segments.includes('-')) {
    return `The pointer '${pointer}' appends to an array, which is not supported; address an existing index.`;
  }

  let current: unknown = templateBody;
  for (const segment of segments.slice(0, -1)) {
    current = Array.isArray(current)
      ? current[Number(segment)]
      : isRecord(current)
        ? current[segment]
        : undefined;

    if (current === undefined || current === null) {
      return `The pointer '${pointer}' has no '${segment}' container in the template body.`;
    }
  }

  if (!isRecord(current) && !Array.isArray(current)) {
    return `The parent of pointer '${pointer}' in the template body is not an object or array.`;
  }

  const last = segments[segments.length - 1] as string;
  if (Array.isArray(current) && !(Number(last) >= 0 && Number(last) < current.length)) {
    return `The pointer '${pointer}' addresses index '${last}', which the template body array does not have.`;
  }

  return undefined;
}

function validatePlaceholders(path: string, boundPathNames: Set<string>): string | undefined {
  const placeholders = [...path.matchAll(PLACEHOLDER)].map((match) => match[1] as string);

  const unbound = placeholders.filter((name) => !boundPathNames.has(name));
  if (unbound.length > 0) {
    return `The path declares placeholder(s) ${unbound.map((n) => `'${n}'`).join(', ')} with no matching binding.`;
  }

  const absent = [...boundPathNames].filter((name) => !placeholders.includes(name));
  if (absent.length > 0) {
    return `Path binding(s) ${absent.map((n) => `'${n}'`).join(', ')} have no matching placeholder in the path.`;
  }

  return undefined;
}

function targetIdentity(target: ClientDynamicBindingTarget): string {
  return target.location === 'body' ? `body '${target.pointer}'` : `${target.location} '${target.name}'`;
}

/**
 * Resolves one `parameterReference` against the designer's current values.
 * `unknown` means the name is not a manifest input at all — an authoring error,
 * distinct from a known input that simply has no value yet.
 */
function resolve(
  parameterReference: string,
  parameterValues: Record<string, unknown>,
  manifestInputNames: ReadonlySet<string>
): Resolution {
  if (manifestInputNames.size > 0 && !manifestInputNames.has(parameterReference)) {
    return { kind: 'unknown' };
  }

  const raw = parameterValues[parameterReference];
  if (raw === undefined || raw === null) return { kind: 'unresolved' };
  if (isExpression(raw)) return { kind: 'unresolved' };

  if (typeof raw === 'string' && raw.length === 0) return { kind: 'unresolved' };

  return { kind: 'value', value: raw };
}

/** Writes a value at an RFC 6901 pointer, preserving its JSON type. */
function setAtPointer(body: unknown, pointer: string, value: unknown): void {
  const segments = pointer.slice(1).split('/').map(decodePointerSegment);

  let current: unknown = body;
  for (const segment of segments.slice(0, -1)) {
    current = Array.isArray(current) ? current[Number(segment)] : (current as Record<string, unknown>)[segment];
  }

  const last = segments[segments.length - 1] as string;
  if (Array.isArray(current)) {
    current[Number(last)] = value;
  } else if (isRecord(current)) {
    current[last] = value;
  }
}

function isScalar(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function appendQueryValue(params: URLSearchParams, name: string, value: unknown): void {
  if (value === undefined || value === null) return;

  if (Array.isArray(value)) {
    for (const entry of value) appendQueryValue(params, name, entry);
    return;
  }

  params.append(name, typeof value === 'string' ? value : String(value));
}

/**
 * Builds the outgoing request from the template and the designer's current values.
 */
export function buildClientDynamicRequest(
  config: ClientDynamicListConfig,
  parameterValues: Record<string, unknown>,
  manifestInputNames: ReadonlySet<string> = new Set()
): ClientDynamicRequestPlan {
  const configError = validateClientDynamicList(config);
  if (configError) return { status: 'error', message: configError };

  const template = config.request.template;
  const bindings = config.request.bindings ?? [];

  let path = template.path;
  const query = new URLSearchParams();
  const body = template.body === undefined ? undefined : structuredClone(template.body);

  const missing: string[] = [];

  const pathValues = new Map<string, string>();
  const queryOverrides = new Map<string, unknown>();

  for (const binding of bindings) {
    const resolution = resolve(binding.parameterReference, parameterValues, manifestInputNames);

    if (resolution.kind === 'unknown') {
      return {
        status: 'error',
        message: `The picker binds to '${binding.parameterReference}', which is not an input of this operation.`,
      };
    }

    if (resolution.kind === 'unresolved') {
      if (binding.required !== false) missing.push(binding.parameterReference);
      continue;
    }

    const { value } = resolution;
    const target = binding.target;

    if (target.location === 'path') {
      if (!isScalar(value)) {
        return {
          status: 'error',
          message: `The binding for '${binding.parameterReference}' must resolve to a string, number or boolean to be placed in the path.`,
        };
      }
      pathValues.set(target.name, encodeURIComponent(String(value)));
    } else if (target.location === 'query') {
      if (!isScalar(value) && !(Array.isArray(value) && value.every(isScalar))) {
        return {
          status: 'error',
          message: `The binding for '${binding.parameterReference}' must resolve to a string, number, boolean, or an array of those to be placed in the query.`,
        };
      }
      queryOverrides.set(target.name, value);
    } else {
      setAtPointer(body, target.pointer, value);
    }
  }

  if (missing.length > 0) return { status: 'waiting', missing };

  for (const [name, encoded] of pathValues) {
    path = path.split(`{${name}}`).join(encoded);
  }

  if (HAS_PLACEHOLDER.test(path)) {
    return { status: 'error', message: `The path '${path}' still contains an unresolved placeholder.` };
  }

  for (const [name, value] of Object.entries(template.query ?? {})) {
    if (!queryOverrides.has(name)) appendQueryValue(query, name, value);
  }
  for (const [name, value] of queryOverrides) {
    appendQueryValue(query, name, value);
  }

  const search = query.toString();
  const url = `${getArmBaseUrl()}${path}${search ? `?${search}` : ''}`;

  return body === undefined
    ? { status: 'ready', url, method: template.method }
    : { status: 'ready', url, method: template.method, body };
}

/** Maps one page of items into combobox options. */
function mapItemsToOptions(items: unknown[], config: ClientDynamicListConfig): DynamicOption[] {
  const mapping = config.response;
  const options: DynamicOption[] = [];

  for (const item of items) {
    const rawValue = getValueByPath(item, mapping.itemValuePath);

    // An option with no value can never be selected meaningfully, and String(undefined)
    // would render the literal text "undefined".
    if (rawValue === undefined || rawValue === null) continue;

    const rawTitle = getValueByPath(item, mapping.itemTitlePath);
    const option: DynamicOption = {
      value: String(rawValue),
      label: rawTitle === undefined || rawTitle === null ? String(rawValue) : String(rawTitle),
    };

    if (mapping.itemDescriptionPath) {
      const description = getValueByPath(item, mapping.itemDescriptionPath);
      if (description !== undefined && description !== null) option.description = String(description);
    }

    if (mapping.itemSelectablePath && getValueByPath(item, mapping.itemSelectablePath) === false) {
      option.disabled = true;
    }

    if (isRecord(item)) option.data = item;

    options.push(option);
  }

  return options;
}

function readItems(document: unknown, itemsPath: string | undefined): unknown[] {
  const items = getValueByPath(document, itemsPath);
  return Array.isArray(items) ? items : [];
}

function readNextLink(document: unknown): string | undefined {
  if (!isRecord(document)) return undefined;
  const next = document['nextLink'] ?? document['@odata.nextLink'];
  return typeof next === 'string' ? next : undefined;
}

/**
 * Resolves a service-supplied `nextLink` — absolute or relative — against the origin
 * the environment resolved, and returns it only if it stays there. Anything else is a
 * way to redirect the user's token.
 */
function resolveSameOriginLink(link: string, origin: string): string | undefined {
  try {
    const resolved = new URL(link, origin);
    return resolved.origin === new URL(origin).origin ? resolved.toString() : undefined;
  } catch {
    return undefined;
  }
}

const MAX_ERROR_DETAIL_LENGTH = 500;

/** Bounded, so a gateway HTML error page cannot flood the parameter's error slot. */
async function readErrorDetail(response: Response): Promise<string | undefined> {
  try {
    const text = (await response.text()).trim();
    if (text.length === 0) return undefined;
    return text.length > MAX_ERROR_DETAIL_LENGTH ? `${text.slice(0, MAX_ERROR_DETAIL_LENGTH)}…` : text;
  } catch {
    return undefined;
  }
}

export interface FetchClientDynamicListParams {
  config: ClientDynamicListConfig;
  plan: Extract<ClientDynamicRequestPlan, { status: 'ready' }>;
  accessToken: string;
  signal?: AbortSignal;
}

/**
 * Issues the planned request with the signed-in user's token and maps the reply.
 * Follows same-origin `nextLink` pages up to the page cap.
 */
export async function fetchClientDynamicListOptions({
  config,
  plan,
  accessToken,
  signal,
}: FetchClientDynamicListParams): Promise<DynamicOption[]> {
  const origin = getArmBaseUrl();
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  if (plan.body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = { method: plan.method, headers };
  if (plan.body !== undefined) init.body = JSON.stringify(plan.body);
  if (signal) init.signal = signal;

  const response = await fetch(plan.url, init);
  if (!response.ok) {
    const detail = await readErrorDetail(response);
    throw new Error(
      detail === undefined
        ? `Request failed (${response.status} ${response.statusText}).`
        : `Request failed (${response.status} ${response.statusText}): ${detail}`
    );
  }

  const document = (await response.json()) as unknown;
  const options = mapItemsToOptions(readItems(document, config.response.itemsPath), config);

  let nextLink = readNextLink(document);
  let pages = 0;

  while (nextLink && pages < MAX_PAGINATION_PAGES) {
    const pageUrl = resolveSameOriginLink(nextLink, origin);
    if (pageUrl === undefined) break;
    pages++;

    const pageInit: RequestInit = { method: 'GET', headers };
    if (signal) pageInit.signal = signal;

    const pageResponse = await fetch(pageUrl, pageInit);
    if (!pageResponse.ok) break;

    const pageDocument = (await pageResponse.json()) as unknown;
    options.push(...mapItemsToOptions(readItems(pageDocument, config.response.itemsPath), config));
    nextLink = readNextLink(pageDocument);
  }

  return options;
}
